import { AlertService } from '@anecdotes/design-system/alert';
import { MonitoringService } from '@anecdotes/monitoring';
import { UpdatesService } from '@anecdotes/service-worker';
import { WindowHelperService, delayedPromise } from '@anecdotes/utils';
import { Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { take, tap } from 'rxjs';
import { UpdatePromptComponent } from '../../../components/update-prompt/update-prompt.component';

@Injectable()
export class ApplicationUpdatesEffects {
  private _isLongSession: boolean;
  private sessionStorage: Storage;

  constructor(
    private updatesService: UpdatesService,
    private alertService: AlertService,
    private monitoringService: MonitoringService,
    private windowHelper: WindowHelperService,
  ) {
    this.sessionStorage = this.windowHelper.getWindow().sessionStorage;

    // This trick makes ability to apply instant update if the user session is not longer than 1 minute
    setTimeout(() => {
      this._isLongSession = true;
    }, 60000);
  }

  updateInstalled$ = createEffect(
    () =>
      this.updatesService.getUpdateInstalledEvent().pipe(
        take(1),
        tap(async () => {
          this.saveInstallationDate();

          if (this.isLongSession()) {
            await new Promise((resolve) => {
              this.alertService.openCustomAlert({
                alertType: 'success',
                component: UpdatePromptComponent,
                componentInput: {
                  activateUpdateCallback: () => resolve(undefined),
                },
              });
            });
          }

          await this.activateUpdateAsync();
        }),
      ),
    { dispatch: false },
  );

  async activateUpdateAsync(): Promise<void> {
    const outdatedUsageInMs = this.getCurrentDate().getTime() - this.getInstallationDate().getTime();
    const outdatedUsageInSec = outdatedUsageInMs / 1000;

    this.monitoringService.sendEvent('Applied application update', { outdatedUsageInSec });
    this.removeInstallationDate();
    await delayedPromise(1000); // we need to ensure
    await this.updatesService.activateUpdateAsync();
  }

  isLongSession(): boolean {
    return this._isLongSession;
  }

  getCurrentDate(): Date {
    return new Date();
  }

  saveInstallationDate(): void {
    if (!this.sessionStorage.getItem('installation_date')) {
      this.sessionStorage.setItem('installation_date', this.getCurrentDate().toISOString());
    }
  }

  removeInstallationDate(): void {
    this.sessionStorage.removeItem('installation_date');
  }

  getInstallationDate(): Date {
    return new Date(this.sessionStorage.getItem('installation_date'));
  }
}
